// src/shared/ai-chat/chat-bridge.ts
// All "page access" features live here: page-context, actions, consent, socket binding.

export type PageContext = {
  url: string; path: string; title: string; selection: string; headings: string[];
  qa: { qa: string; role: "button"|"link"|"input"|"other"; text: string; enabled: boolean }[];
  metas: Record<string,string>; viewport:{ w:number; h:number }; timestamp: number;
};

export type ActionResult = { ok: boolean; data?: any; error?: string };
export type ActionDef<Args=any, Res=any> = {
  description: string;
  validate?: (args:any, ctx:{page:PageContext}) => boolean;
  handler: (args:Args, ctx:{page:PageContext}) => Promise<Res>|Res;
};

type ConsentFn = (params: { action: string; args: any; explain?: string }) => Promise<"allow"|"deny"|"always">;

type BridgeOpts = {
  getSocket: () => any | null;               // () => Socket | null
  getSessionId: () => string | null;         // () => string | null
  consentPrompt?: ConsentFn;                  // how to ask the user (you choose the UI)
  rememberKey?: string;                       // localStorage key for "always allow" map
  autoEmitOnRoute?: boolean;                  // re-emit context on SPA navigation
};

const rememberDefaultKey = "__ai_action_allow";

const roleOf = (el: HTMLElement): "button"|"link"|"input"|"other" =>
  el.matches("button,[role=button]") ? "button" :
  el.matches("a,[role=link]") ? "link" :
  el.matches("input,textarea,select,[contenteditable=true]") ? "input" : "other";

const isVisible = (el: HTMLElement) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
const clip = (s:string,n:number)=> s.length>n? s.slice(0,n)+"…": s;

export function collectPageContext(maxQa=200): PageContext {
  const selection = (window.getSelection?.()?.toString() || "").trim();
  const headings = Array.from(document.querySelectorAll("h1,h2,h3"))
    .map(h => (h.textContent||"").trim()).filter(Boolean).slice(0,50);

  const qa = Array.from(document.querySelectorAll<HTMLElement>("[data-qa]"))
    .filter(isVisible).slice(0,maxQa).map(el=>({
      qa: el.dataset.qa || "", role: roleOf(el),
      text: clip((el.innerText || el.textContent || "").replace(/\s+/g," ").trim(), 140),
      enabled: !(el as HTMLButtonElement).disabled,
    }));

  const metas: Record<string,string> = {};
  ["description","keywords","og:title","og:description"].forEach(name=>{
    const m = document.querySelector(`meta[name="${name}"]`) ||
              document.querySelector(`meta[property="${name}"]`);
    if (m) metas[name] = (m.getAttribute("content")||"").slice(0,500);
  });

  return {
    url: location.href,
    path: location.pathname + location.search + location.hash,
    title: document.title,
    selection, headings, qa, metas,
    viewport:{ w: innerWidth, h: innerHeight },
    timestamp: Date.now(),
  };
}

/* ---------------- Action registry (local to the page) ---------------- */

const registry = new Map<string, ActionDef>();

export function registerAction<Args,Res>(name: string, def: ActionDef<Args,Res>) {
  registry.set(name, def as ActionDef);
  return () => registry.delete(name);
}
export function listActions() {
  return Array.from(registry.entries()).map(([name, def]) => ({ name, description: def.description }));
}
async function executeAction(name:string, args:any, ctx:{page:PageContext}): Promise<ActionResult> {
  const def = registry.get(name);
  if (!def) return { ok:false, error:`Unknown action: ${name}` };
  if (def.validate && !def.validate(args, ctx)) return { ok:false, error:`Invalid args for ${name}` };
  try { const data = await def.handler(args, ctx); return { ok:true, data }; }
  catch(e:any){ return { ok:false, error: e?.message || String(e) }; }
}

/* ---------------- Default DOM actions (registered only when enabled) ---------------- */

function registerDefaultDomActions() {
  const cleanups = [
    registerAction("dom.clickByQa", {
      description: "Click an element by data-qa",
      validate: a => a && typeof a.qa === "string",
      handler: async ({ qa }: { qa:string }) => {
        const el = document.querySelector<HTMLElement>(`[data-qa="${qa}"]`);
        if (!el) throw new Error(`No element with data-qa="${qa}"`);
        el.click?.(); el.dispatchEvent(new Event("click", { bubbles: true }));
        return { clicked: qa };
      },
    }),
    registerAction("dom.fillInputByQa", {
      description: "Fill an input/textarea by data-qa",
      validate: a => a && typeof a.qa === "string" && "value" in a,
      handler: async ({ qa, value }: { qa:string; value:string }) => {
        const input = document.querySelector<HTMLInputElement|HTMLTextAreaElement>(`[data-qa="${qa}"]`);
        if (!input) throw new Error(`No input with data-qa="${qa}"`);
        input.focus(); (input as HTMLInputElement).value = value;
        input.dispatchEvent(new Event("input",{bubbles:true}));
        input.dispatchEvent(new Event("change",{bubbles:true}));
        return { filled: qa, valueSample: String(value).slice(0, 20) };
      },
    }),
    registerAction("dom.scrollToQa", {
      description: "Scroll element into view",
      validate: a => a && typeof a.qa === "string",
      handler: async ({ qa }: { qa:string }) => {
        const el = document.querySelector<HTMLElement>(`[data-qa="${qa}"]`);
        if (!el) throw new Error(`No element with data-qa="${qa}"`);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return { scrolledTo: qa };
      },
    }),
    registerAction("nav.goUrl", {
      description: "Navigate to a URL",
      validate: a => a && typeof a.url === "string",
      handler: async ({ url }: { url:string }) => { window.location.assign(url); return { navigated: url }; },
    }),
  ];
  return () => cleanups.forEach(fn => fn());
}

/* ---------------- Bridge factory ---------------- */

export function createChatBridge(opts: BridgeOpts) {
  const consent = opts.consentPrompt || (async () => "deny" as const);
  const rememberKey = opts.rememberKey || rememberDefaultKey;

  // remembered allow map
  const allowMap: Record<string, boolean> = (() => {
    try { return JSON.parse(localStorage.getItem(rememberKey) || "{}"); } catch { return {}; }
  })();
  const setRemember = (action: string, allow: boolean) => {
    allowMap[action] = allow;
    try { localStorage.setItem(rememberKey, JSON.stringify(allowMap)); } catch {}
  };

  let enabled = false;
  let domCleanup: null | (() => void) = null;

  // SPA route hooks
  const bindRouteEmitter = () => {
    const s = opts.getSocket();
    const sessionId = opts.getSessionId();
    if (!s || !sessionId) return;

    const emitContext = () => {
      if (!enabled) return;
      s.emit("page_context", {
        sessionId,
        pageContext: collectPageContext(),
        availableActions: listActions(),
      });
    };

    // initial
    emitContext();

    const _push = history.pushState;
    const _replace = history.replaceState;
    history.pushState = function (...args) { _push.apply(this, args as any); emitContext(); };
    history.replaceState = function (...args) { _replace.apply(this, args as any); emitContext(); };
    const pop = () => emitContext();
    window.addEventListener("popstate", pop);

    return () => {
      history.pushState = _push;
      history.replaceState = _replace;
      window.removeEventListener("popstate", pop);
    };
  };

  let unbindRoutes: null | (() => void) = null;

  function enablePageAccess(flag: boolean) {
    enabled = flag;
    // reset registry and (re)register defaults
    registry.clear();
    if (enabled) {
      domCleanup = registerDefaultDomActions();
      if (opts.autoEmitOnRoute) {
        unbindRoutes?.();
        unbindRoutes = bindRouteEmitter() || null;
      }
      // send one immediate context snapshot (if socket/session ready)
      const s = opts.getSocket(); const sessionId = opts.getSessionId();
      if (s && sessionId) {
        s.emit("page_context", {
          sessionId,
          pageContext: collectPageContext(),
          availableActions: listActions(),
        });
      }
    } else {
      domCleanup?.(); domCleanup = null;
      unbindRoutes?.(); unbindRoutes = null;
    }
  }

  function isEnabled() { return enabled; }

  // Build outgoing payload additions
  function buildPayloadExtras() {
    if (!enabled) return { pageContext: null, availableActions: [] as any[] };
    return { pageContext: collectPageContext(), availableActions: listActions() };
  }

  // Bind socket listeners for action requests
  function bindSocket(socket: any) {
    socket.on("action_request", async (data: { requestId:string; action:string; args:any; explain?:string }) => {
      if (!enabled) {
        socket.emit("action_result", { requestId: data.requestId, ok:false, error:"Page access is disabled" });
        return;
      }
      const page = collectPageContext();

      // already remembered?
      if (allowMap[data.action]) {
        const result = await executeAction(data.action, data.args, { page });
        socket.emit("action_result", { requestId: data.requestId, ...result });
        return;
      }

      // ask user
      const decision = await consent({ action: data.action, args: data.args, explain: data.explain });
      if (decision === "deny") {
        socket.emit("action_result", { requestId: data.requestId, ok:false, error:"User denied" });
        return;
      }
      if (decision === "always") setRemember(data.action, true);

      const result = await executeAction(data.action, data.args, { page });
      socket.emit("action_result", { requestId: data.requestId, ...result });
    });
  }

  return {
    enablePageAccess,
    isEnabled,
    buildPayloadExtras,
    bindSocket,
  };
}
